import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Search, CheckCircle2, XCircle, ShieldCheck, Users, Sparkles } from 'lucide-react';
import SeatBulkClearanceDialog from './SeatBulkClearanceDialog';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

export default function SeatClearancePanel({ onChange }) {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all|approved|pending
  const [selected, setSelected] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);
  const debouncer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '20000');
      const res = await api.get(`/seat/clearance?${params.toString()}`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error('Failed to load clearances');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [q, statusFilter]);

  useEffect(() => {
    if (debouncer.current) clearTimeout(debouncer.current);
    debouncer.current = setTimeout(load, 300);
    return () => clearTimeout(debouncer.current);
  }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllVisible = () => {
    const ids = items.filter(i => i.status !== 'approved').map(i => i.hof_id);
    setSelected(new Set(ids));
  };
  const clearSel = () => setSelected(new Set());

  const approve = async (hof_id) => {
    setBusyId(hof_id);
    try {
      await api.post(`/seat/clearance/${hof_id}/approve`);
      toast.success('Approved');
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(''); }
  };
  const remove = async (hof_id) => {
    setBusyId(hof_id);
    try {
      await api.post(`/seat/clearance/${hof_id}/remove`);
      toast.success('Removed');
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(''); }
  };
  const bulkApprove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Approve ${selected.size} HOF(s)?`)) return;
    try {
      const res = await api.post('/seat/clearance/bulk-approve', { hof_ids: Array.from(selected) });
      toast.success(res.data?.message || 'Done');
      clearSel();
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk approve failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search HOF name / ITS / mobile…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid="clearance-search"
          />
        </div>
        <div className="inline-flex rounded-md border bg-white">
          {[['all', 'All'], ['pending', 'Pending'], ['approved', 'Approved']].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setStatusFilter(v)}
              className={`px-3 py-1.5 text-xs font-medium ${statusFilter === v ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
              data-testid={`clr-filter-${v}`}
            >{l}</button>
          ))}
        </div>
        <Badge variant="outline" className="px-2 py-1">{total} total</Badge>
        <Button size="sm" onClick={() => setShowBulk(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="open-bulk-clearance">
          <Sparkles className="w-3 h-3 mr-1" /> Bulk Operations
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
          <ShieldCheck className="w-4 h-4 text-emerald-700" />
          <span className="text-sm text-emerald-800 flex-1">{selected.size} HOF(s) selected</span>
          <Button size="sm" variant="outline" onClick={clearSel}>Clear</Button>
          <Button size="sm" onClick={bulkApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="clr-bulk-approve">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Bulk Approve
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No HOFs match</CardContent></Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-2 py-2 w-8">
                    <button onClick={selectAllVisible} className="text-emerald-700 hover:underline text-[10px]">Sel all pending</button>
                  </th>
                  <th className="px-3 py-2 text-left">HOF</th>
                  <th className="px-3 py-2 text-left hidden md:table-cell">Sector / Mohalla</th>
                  <th className="px-3 py-2 text-center">Ladies</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((it) => (
                  <tr key={it.hof_id} className="hover:bg-slate-50" data-testid={`clr-row-${it.hof_id}`}>
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(it.hof_id)}
                        onChange={() => toggleSelect(it.hof_id)}
                        disabled={it.status === 'approved'}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{it.hof_name}</div>
                      <div className="text-xs text-slate-500">{it.hof_its_id || '—'} · {it.mobile || ''}</div>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell text-xs text-slate-600">
                      {it.sector || '—'}{it.mohalla ? ` / ${it.mohalla}` : ''}
                    </td>
                    <td className="px-3 py-2 text-center font-bold">{it.eligible_ladies}</td>
                    <td className="px-3 py-2 text-center">
                      {it.status === 'approved' ? (
                        <Badge className="bg-emerald-600">Approved</Badge>
                      ) : it.status === 'removed' ? (
                        <Badge className="bg-red-500">Removed</Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300">Pending</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center space-x-1">
                      {it.status === 'approved' ? (
                        <Button size="sm" variant="outline" className="text-red-600 h-7"
                          onClick={() => remove(it.hof_id)} disabled={busyId === it.hof_id}
                          data-testid={`clr-remove-${it.hof_id}`}>
                          {busyId === it.hof_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><XCircle className="w-3 h-3 mr-1" />Remove</>}
                        </Button>
                      ) : (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7"
                          onClick={() => approve(it.hof_id)} disabled={busyId === it.hof_id}
                          data-testid={`clr-approve-${it.hof_id}`}>
                          {busyId === it.hof_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" />Approve</>}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SeatBulkClearanceDialog
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onComplete={() => { load(); onChange?.(); }}
      />
    </div>
  );
}
